import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Clapperboard } from "lucide-react";
import { resetPassword } from "../lib/api";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await resetPassword({ token, password });
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Could not reset your password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="reset-password-page" className="min-h-screen flex items-center justify-center px-4 relative overflow-hidden">
      <motion.div
        className="absolute inset-0"
        style={{ background: "radial-gradient(circle at center, #18181b 0%, #000000 70%)" }}
        animate={{ opacity: [0.5, 1, 0.5] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 w-full max-w-md"
      >
        <div className="flex items-center gap-3 justify-center mb-10">
          <Clapperboard className="w-5 h-5 text-white" strokeWidth={1.5} />
          <span className="font-meta text-xs tracking-[0.4em] uppercase text-white">PurePlay</span>
        </div>
        <div className="bg-[#050505] border border-white/10 rounded-2xl p-8 md:p-10">
          {done ? (
            <div data-testid="reset-success">
              <h1 className="font-heading text-3xl text-white tracking-tight">All set.</h1>
              <p className="mt-3 text-sm text-zinc-500">Your password has been updated. Sign in with your new password.</p>
              <button
                data-testid="reset-goto-login"
                onClick={() => navigate("/login")}
                className="mt-8 w-full bg-white text-black rounded-full py-3.5 font-meta text-xs uppercase tracking-[0.25em] hover:scale-[1.02] active:scale-[0.98] transition-transform duration-200"
              >
                Sign In
              </button>
            </div>
          ) : !token ? (
            <div data-testid="reset-invalid">
              <h1 className="font-heading text-3xl text-white tracking-tight">Invalid link.</h1>
              <p className="mt-3 text-sm text-zinc-500">This reset link is missing its token. Request a fresh one from the sign-in page.</p>
              <Link to="/login" data-testid="reset-back-to-login" className="mt-8 block text-center w-full border border-white/15 rounded-full py-3.5 font-meta text-xs uppercase tracking-[0.25em] text-zinc-300 hover:text-white hover:border-white/40 transition-colors duration-200">
                Back to Sign In
              </Link>
            </div>
          ) : (
            <>
              <h1 className="font-heading text-3xl text-white tracking-tight">New password.</h1>
              <p className="mt-2 font-meta text-[11px] uppercase tracking-[0.25em] text-zinc-500">Choose a fresh password</p>
              <form onSubmit={submit} className="mt-8 flex flex-col gap-4">
                <input
                  data-testid="reset-password-input"
                  type="password"
                  placeholder="New password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-5 py-3.5 text-white placeholder-zinc-500 focus:outline-none focus:border-white/30 focus:bg-white/10 transition-colors duration-300 font-ui text-sm"
                />
                <input
                  data-testid="reset-confirm-input"
                  type="password"
                  placeholder="Confirm new password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  minLength={6}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-5 py-3.5 text-white placeholder-zinc-500 focus:outline-none focus:border-white/30 focus:bg-white/10 transition-colors duration-300 font-ui text-sm"
                />
                {error && (
                  <p data-testid="reset-error" className="text-red-400 text-sm">
                    {error}
                  </p>
                )}
                <button
                  data-testid="reset-submit-button"
                  type="submit"
                  disabled={loading}
                  className="mt-2 w-full bg-white text-black rounded-full py-3.5 font-meta text-xs uppercase tracking-[0.25em] hover:scale-[1.02] active:scale-[0.98] transition-transform duration-200 disabled:opacity-50"
                >
                  {loading ? "One moment…" : "Reset Password"}
                </button>
              </form>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}
